/**
 * Pure derivation of the StatusPill state from a `driveState` row, for
 * whichever backend is active. `providerLabel` only reaches the two strings
 * that genuinely differ per provider; everything else says "Lagring".
 * Kept free of React so it can be unit tested.
 */
import type { DriveState, IsoDate } from '@/domain/types';
import { STORAGE } from '../strings';
import { formatDateTime, formatTime, todayIso } from '../format';

export type StorageStatusKind = 'ok' | 'pending' | 'needs_sign_in' | 'disconnected' | 'error' | 'conflict' | 'offline' | 'loading';
export type StorageStatusTone = 'ok' | 'gold' | 'grey' | 'danger';
/** `resolve` opens the provider page, where a numbering collision is settled. */
export type StorageStatusAction = 'connect' | 'sign_in' | 'retry' | 'resolve' | null;

export interface StorageStatus {
  kind: StorageStatusKind;
  tone: StorageStatusTone;
  label: string;
  /** Inline action label shown in the pill, if any. */
  actionLabel: string | null;
  action: StorageStatusAction;
  /** Days since the last successful backup, when known. */
  staleDays: number | null;
}

export function deriveStorageStatus(
  state: DriveState | null | undefined,
  online: boolean,
  today: IsoDate = todayIso(),
  providerLabel = 'Google Drive',
): StorageStatus {
  if (state === undefined) {
    return { kind: 'loading', tone: 'grey', label: '…', actionLabel: null, action: null, staleDays: null };
  }
  const staleDays = state?.lastBackupAt ? Math.max(0, daysSince(state.lastBackupAt, today)) : null;
  if (!state || !state.folders) {
    return { kind: 'disconnected', tone: 'grey', label: STORAGE.disconnected, actionLabel: STORAGE.connect, action: 'connect', staleDays };
  }
  if (state.ledgerConflict) {
    return { kind: 'conflict', tone: 'danger', label: STORAGE.conflict, actionLabel: STORAGE.resolve, action: 'resolve', staleDays };
  }
  if (state.lastError) {
    return { kind: 'error', tone: 'danger', label: STORAGE.error(state.lastError), actionLabel: 'Försök igen', action: 'retry', staleDays };
  }
  if (state.needsSignIn) {
    return { kind: 'needs_sign_in', tone: 'gold', label: STORAGE.needsSignIn(providerLabel), actionLabel: STORAGE.signIn, action: 'sign_in', staleDays };
  }
  if (!online && state.pendingChanges > 0) {
    return { kind: 'offline', tone: 'gold', label: STORAGE.offline, actionLabel: null, action: null, staleDays };
  }
  if (state.pendingChanges > 0) {
    return { kind: 'pending', tone: 'gold', label: STORAGE.pending(state.pendingChanges), actionLabel: null, action: null, staleDays };
  }
  if (state.lastBackupAt) {
    const when = daysSince(state.lastBackupAt, today) === 0 ? formatTime(state.lastBackupAt) : formatDateTime(state.lastBackupAt, today);
    return { kind: 'ok', tone: 'ok', label: STORAGE.backedUp(when), actionLabel: null, action: null, staleDays };
  }
  return { kind: 'pending', tone: 'gold', label: 'Ingen säkerhetskopia ännu', actionLabel: null, action: null, staleDays };
}

function daysSince(at: string, today: IsoDate): number {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return 0;
  const local = todayIso(d);
  const a = Date.UTC(Number(local.slice(0, 4)), Number(local.slice(5, 7)) - 1, Number(local.slice(8, 10)));
  const b = Date.UTC(Number(today.slice(0, 4)), Number(today.slice(5, 7)) - 1, Number(today.slice(8, 10)));
  return Math.round((b - a) / 86_400_000);
}
